import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FiGrid,
  FiPackage,
  FiMessageSquare,
  FiBell,
  FiShoppingBag
} from 'react-icons/fi';
import { FaWallet } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationCount } from '../contexts/NotificationCountContext';
import { useSocket } from '../contexts/SocketContext';
import axiosInstance from '../utils/api';

// Keyframes for animations
const pulseGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 0 0 rgba(255, 165, 0, 0.7);
    transform: scale(1);
  }
  50% { 
    box-shadow: 0 0 0 8px rgba(255, 165, 0, 0);
    transform: scale(1.05);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const badgePulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const BottomNavbar = () => {
  const location = useLocation();
  const { user, userMode, authState } = useAuth();
  const { unreadCount: unreadNotificationCount } = useNotificationCount();
  const socket = useSocket();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // All hooks must be called before any conditional returns
  const isGuest = !user;

  // Responsive breakpoints - keeping bottom nav on all devices
  const navLayout = 'horizontal'; // Always horizontal
  const iconSize = useBreakpointValue({ base: 6, md: 7, lg: 8 });
  const containerPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const itemSpacing = useBreakpointValue({ base: 1, md: 2, lg: 4 });
  const navHeight = useBreakpointValue({ base: 'auto', lg: '80px' });
  const maxWidth = useBreakpointValue({ base: '400px', md: '600px', lg: '800px' });

  // Theme colors
  const bg = useColorModeValue(
    'rgba(255, 255, 255, 0.96)',
    'rgba(26, 32, 44, 0.96)'
  );
  const activeColor = useColorModeValue('forest.700', 'forest.400');
  const inactiveColor = useColorModeValue('warm.600', 'gray.400');
  const shadowColor = useColorModeValue(
    'rgba(8, 28, 21, 0.08)',
    'rgba(0, 0, 0, 0.3)'
  );
  const borderColor = useColorModeValue(
    'surface.border',
    'rgba(45, 55, 72, 0.8)'
  );

  // Memoized navigation items
  const navigation = useMemo(() => [
    {
      name: userMode === 'seller' ? 'Dashboard' : 'Orders',
      path: userMode === 'seller' ? '/seller-dashboard' : '/buyer-dashboard',
      icon: userMode === 'seller' ? FiGrid : FiShoppingBag,
      label: userMode === 'seller' ? 'Dashboard' : 'Orders'
    },
    ...(userMode !== 'seller' ? [{
      name: 'Products',
      path: '/product-listing',
      icon: FiPackage,
      label: 'Products'
    }] : []),
    {
      name: 'Messages',
      path: '/messages',
      icon: FiMessageSquare,
      label: 'Messages'
    },
    {
      name: 'Notifications',
      path: '/notifications',
      icon: FiBell,
      label: 'Notifications'
    },
    ...(userMode === 'seller' ? [{
      name: 'Wallet',
      path: '/wallet',
      icon: FaWallet,
      label: 'Wallet'
    }] : [])
  ], [userMode]);

  // Fetch unread message count
  const fetchUnreadMessageCount = useCallback(async () => {
    if (!user) return;

    try {
      const response = await axiosInstance.get('/api/messages/unread/count');
      setUnreadMessageCount(parseInt(response.data.unreadCount) || 0);
    } catch (error) {
      console.error('Failed to fetch unread message count:', error);
    }
  }, [user]);

  // Initial message count fetch
  useEffect(() => {
    fetchUnreadMessageCount();
  }, [fetchUnreadMessageCount]);

  // Socket event handlers
  useEffect(() => {
    if (!user || !socket || !authState) return;

    const handleMessageReceived = () => {
      setUnreadMessageCount(prev => prev + 1);
    };

    const handleChatOpened = () => {
      setUnreadMessageCount(prev => Math.max(0, prev - 1));
    };

    socket.on('messageReceived', handleMessageReceived);
    socket.on('chatOpened', handleChatOpened);

    return () => {
      socket.off('messageReceived', handleMessageReceived);
      socket.off('chatOpened', handleChatOpened);
    };
  }, [user, socket, authState]);

  // Badge component
  const Badge = ({ count, color = 'red.500' }) => (
    <Box
      position="absolute"
      top="-6px"
      right="-6px"
      bg={color}
      color="white"
      fontSize={{ base: '10px', lg: '11px' }}
      fontWeight="700"
      borderRadius="full"
      minW={{ base: '18px', lg: '22px' }}
      h={{ base: '18px', lg: '22px' }}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px="4px"
      border="2px solid"
      borderColor={bg}
      animation={`${badgePulse} 2s infinite`}
      zIndex={2}
    >
      {count > 99 ? '99+' : count}
    </Box>
  );

  // Navigation item component
  const NavItem = ({ item, isActive, onClick }) => {
    const isMessages = item.name === 'Messages';
    const isNotifications = item.name === 'Notifications';
    const showMessageBadge = isMessages && unreadMessageCount > 0;
    const showNotificationBadge = isNotifications && unreadNotificationCount > 0;

    return (
      <Link
        to={item.path}
        onClick={onClick}
        style={{ textDecoration: 'none' }}
      >
        <Box
          position="relative"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={{ base: 2, md: 3, lg: 4 }}
          px={{ base: 3, md: 4, lg: 6 }}
          minW={{ base: '60px', md: '80px', lg: '100px' }}
          minH={{ base: '48px', lg: '70px' }}
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          borderRadius={{ base: '16px', lg: '20px' }}
          _hover={{
            bg: useColorModeValue('gray.50', 'gray.700'),
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 25px ${shadowColor}`,
          }}
          _active={{
            transform: 'translateY(-2px)',
          }}
          animation={'none'}
          role="button"
          aria-label={item.label}
          tabIndex={0}
        >
          {/* Active indicator */}
          {isActive && (
            <Box
              position="absolute"
              top="-8px"
              left="50%"
              transform="translateX(-50%)"
              w={{ base: '4px', lg: '6px' }}
              h={{ base: '4px', lg: '6px' }}
              bg={activeColor}
              borderRadius="full"
              boxShadow={`0 0 12px ${activeColor}`}
            />
          )}

          {/* Icon container */}
          <Box position="relative" mb={{ base: 1, lg: 2 }}>
            <Icon
              as={item.icon}
              boxSize={isActive ? iconSize + 1 : iconSize}
              color={isActive ? activeColor : inactiveColor}
              transition="all 0.3s ease"
              filter={isActive ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' : 'none'}
            />

            {/* Badges */}
            {showMessageBadge && <Badge count={unreadMessageCount} />}
            {showNotificationBadge && <Badge count={unreadNotificationCount} />}
          </Box>

          {/* Label */}
          <Text
            fontSize={{ base: '10px', md: '11px', lg: '13px' }}
            fontWeight={isActive ? '600' : '500'}
            color={isActive ? activeColor : inactiveColor}
            transition="all 0.3s ease"
            lineHeight="1.2"
            textAlign="center"
            opacity={isActive ? 1 : 0.8}
            letterSpacing={{ base: '0', lg: '0.5px' }}
          >
            {item.label}
          </Text>
        </Box>
      </Link>
    );
  };

  // Return guest navigation if user is not authenticated
  if (isGuest) {
    return null;
  }

  // Enhanced Bottom Navigation for authenticated users
  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={1000}
      animation={`${slideUp} 0.3s ease-out`}
    >
      {/* Backdrop blur effect */}
      <Box
        position="absolute"
        inset={0}
        bg={bg}
        backdropFilter="blur(20px)"
        borderTop="1px solid"
        borderColor={borderColor}
        borderTopRadius={{ base: '24px', lg: '32px' }}
        boxShadow={`0 -4px 20px ${shadowColor}`}
      />

      {/* Navigation content */}
      <Box position="relative" px={containerPadding} py={{ base: 2, lg: 4 }}>
        <Flex
          justify="space-around"
          align="center"
          maxW={maxWidth}
          mx="auto"
          gap={itemSpacing}
          minH={navHeight}
        >
          {navigation.map((item) => (
            <NavItem
              key={item.name}
              item={item}
              isActive={location.pathname === item.path}
            />
          ))}
        </Flex>
      </Box>

      {/* Bottom safe area for iOS */}
      <Box h="env(safe-area-inset-bottom)" />
    </Box>
  );
};

export default BottomNavbar;
